// Copyright ©️ 2026 eVolve MEP, LLC

import './analytics.css';

import React, { useMemo, useEffect, useState, useCallback } from 'react';

import { type VDCOSChartToolType, type StrongOmit, useEvolveGet } from '@evolvemep/api-ts';
import { Box, Card, Flex, Overlay, Text } from '@mantine/core';
import { useParams } from 'react-router';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { PRODUCT_IDS } from 'constants/globalConstants';
import { strongIsEqual } from 'helpers/strongEntries';
import { useActionLocalStorage, useLocalStorage } from 'hooks/useLocalStorage';
import { useUser } from 'hooks/user/useUser';
import { AnalyticsFilters, type AnalyticsFiltersProps } from 'modules/Shop/Insights/RevitUsage/AnalyticsFilters';
import { ExportAnalyticsButton } from 'modules/Shop/Insights/RevitUsage/ExportAnalyticsButton';
import { InstalledEvolveVersionsChart } from 'modules/Shop/Insights/RevitUsage/views/charts/InstalledEvolveVersionsChart';
import { DocumentSyncTimesChart } from 'modules/Shop/Insights/RevitUsage/views/charts/SyncTimes/DocumentSyncTimesChart';
import { VersionRequiredHelp } from 'modules/Shop/Insights/VersionRequiredHelp';

import type { AnalyticsChartProps, AnalyticsViewProps } from './types';
import {
  ChartFiltersContext,
  useChartFilters,
  type VDCOSChartOptionalFilters,
  chartToolTypeLabels,
  type VDCOSChartFilters,
} from './useChartFilters';
import { EventsByDateChart } from './views/charts/EventsByDateChart';
import { EventsPerUserChart } from './views/charts/EventsPerUserChart';
import { FeatureUsageChart } from './views/charts/FeatureUsageChart';
import { FeatureAnalysisView } from './views/FeatureAnalysisView';
import { FeatureHeatmapView } from './views/FeatureHeatmapView';
import { FeatureUsageView } from './views/FeatureUsageView';
import { InstalledVersionsView } from './views/InstalledVersionsView';

type AnalyticsView = {
  route: string;
  title: string;
  description: (filters: VDCOSChartFilters) => string;
  Component: React.FC<AnalyticsViewProps>;
  DemoComponent: React.FC<AnalyticsChartProps<unknown>>;
  /** Filters necessary for this view (both displayed and included in BE call) */
  usedFilters: (keyof VDCOSChartFilters)[];
  toolTypes: VDCOSChartToolType[];
};

export const charts = [
  {
    route: 'events-per-user',
    title: 'Feature Usage',
    description: (filters) => `View total ${chartToolTypeLabels[filters.toolType]} usage by member`,
    Component: FeatureUsageView,
    DemoComponent: EventsPerUserChart,
    usedFilters: ['timeFrame', 'userIds', 'toolType'],
    toolTypes: ['Evolve', 'Revit'],
  },
  {
    route: 'feature-analysis',
    title: 'In-depth Feature Analysis',
    description: (filters) => `Visualize ${chartToolTypeLabels[filters.toolType]} usage over time`,
    Component: FeatureAnalysisView,
    DemoComponent: EventsByDateChart,
    usedFilters: ['timeFrame', 'userIds', 'featureTypeNames', 'toolType'],
    toolTypes: ['Evolve', 'Revit'],
  },
  {
    route: 'feature-heatmap',
    title: 'Feature Heatmap',
    description: (filters) => `A snapshot of the most popular ${chartToolTypeLabels[filters.toolType]} features used`,
    Component: FeatureHeatmapView,
    DemoComponent: FeatureUsageChart,
    usedFilters: ['timeFrame', 'userIds', 'toolType'],
    toolTypes: ['Evolve', 'Revit'],
  },
  {
    route: 'installed-versions',
    title: 'Installed Versions',
    description: (filters) =>
      `Track installed ${chartToolTypeLabels[filters.toolType]} versions to ensure team-wide consistency`,
    Component: InstalledVersionsView,
    DemoComponent: InstalledEvolveVersionsChart,
    usedFilters: ['timeFrame', 'selectedRevitYear', 'toolType'],
    toolTypes: ['Evolve', 'Revit'],
  },
  {
    route: 'user-sync-times',
    title: 'User Sync Times',
    description: () => 'View the time it takes for users to sync documents',
    Component: DocumentSyncTimesChart,
    DemoComponent: DocumentSyncTimesChart,
    usedFilters: ['timeFrame', 'userIds', 'toolType'],
    toolTypes: ['Revit'],
  },
] as const satisfies AnalyticsView[];

export type ChartRoute = (typeof charts)[number]['route'];

const AnalyticsLandingPageWrapped = (props: StrongOmit<AnalyticsFiltersProps, 'usedFilters'>) => {
  const filters = useChartFilters();
  const navigate = useEvolveNavigate('VDC Insights');

  const chartsToRender = useMemo(
    () => charts.filter(({ toolTypes }) => toolTypes.some((toolType) => toolType === filters.toolType)),
    [filters.toolType],
  );

  const { chartId } = useParams();
  const chart = charts.find((c) => c.route === chartId);
  if (chart) {
    const { Component, title } = chart;
    const usedFilters = chart.usedFilters as (keyof VDCOSChartFilters)[];
    const filtersAreSet = usedFilters.some((filter) =>
      filter === 'selectedRevitYear' || filter === 'timeFrame' || filter === 'toolType'
        ? false
        : filters[filter].length > 0,
    );
    return (
      <>
        <BasePageHeader
          title={title}
          parentPage={{
            title: 'VDC Insights',
            url: '/analytics',
          }}
          hideGlobalFilters
          topRightComponent={<AnalyticsFilters {...props} filtersAreSet={filtersAreSet} usedFilters={usedFilters} />}
        />
        <Component title={title} usedFilters={usedFilters} />
      </>
    );
  }

  return (
    <>
      <BasePageHeader
        title="VDC Insights"
        hideGlobalFilters
        topRightComponent={
          <>
            <ExportAnalyticsButton timeFrame={filters.timeFrame} />
            <AnalyticsFilters {...props} />
          </>
        }
      />
      <VersionRequiredHelp versionRequired="8.2.6" />
      <Box h="100%" style={{ overflowY: 'auto' }} pb="xl">
        <Flex wrap="wrap" justify="space-between" rowGap="md" pr="xs" style={{ overflow: 'visible' }}>
          {chartsToRender.map(({ title, description, route, DemoComponent, usedFilters }) => (
            <Card
              key={title}
              onClick={() => navigate(`/analytics/${route}`)}
              style={{ cursor: 'pointer', width: '49%' }}
              withBorder
              shadow="md"
              h={365}
            >
              <Text fz="lg" fw={500}>
                {title}
              </Text>
              <Text fz="sm" c="dimmed">
                {description(filters)}
              </Text>
              <Box pos="relative" flex={1}>
                <DemoComponent usedFilters={usedFilters} showDemo />
              </Box>
              <Overlay className="analytics-card" />
            </Card>
          ))}
        </Flex>
      </Box>
    </>
  );
};

export const AnalyticsLandingPage = () => {
  const [pendingFilters, setPendingFilters] = useActionLocalStorage(
    'VDC_OS_ANALYTICS_FILTERS',
    (currentFilters, newFilters: Partial<VDCOSChartOptionalFilters>) => ({
      ...currentFilters,
      ...newFilters,
    }),
    {
      selectedRevitYear: null,
      featureTypeNames: [],
      userIds: [],
    },
  );
  const [filters, setFiltersInternal] = useState(pendingFilters);
  const applyFilters = useCallback(() => setFiltersInternal(structuredClone(pendingFilters)), [pendingFilters]);
  const discardPendingFilters = useCallback(() => setPendingFilters(filters), [filters, setPendingFilters]);

  useEffect(() => {
    // If the lookup for `selectedRevitYear` finished,
    // and we haven't set a revit year on our filters,
    // update them to the pending filters.
    if (pendingFilters['selectedRevitYear'] && !filters['selectedRevitYear']) {
      applyFilters();
    }
  }, [pendingFilters, filters, applyFilters]);

  const hasPendingChanges = useMemo(() => !strongIsEqual(filters, pendingFilters), [filters, pendingFilters]);

  const { licenses } = useUser();
  const { data: platformMessageText } = useEvolveGet('admin/platformMessage', {
    lazy: false,
    defaultConfig: { params: { keyName: 'MissingRevitUsageHistoryLicenseShort' } },
  });

  const timeFrameGroups = useMemo(() => {
    const hasUsageAnalyticsLicense = licenses.some((productId) => productId === PRODUCT_IDS.REVIT_USAGE_HISTORY);
    return [
      { group: '', items: ['Last Week', 'This Week'] as const },
      {
        group: hasUsageAnalyticsLicense
          ? ''
          : (platformMessageText?.value ?? 'Available with license').replace(/\.$/, ''),
        disabled: !hasUsageAnalyticsLicense,
        items: ['This Month', 'Last Year', 'Last Month', 'Specific Date', 'Date Range'] as const,
      },
    ];
  }, [platformMessageText, licenses]);

  const { timeFrame } = useGlobalFilters({
    timeFrame: {
      defaultTimeFrame: 'This Week',
      groups: timeFrameGroups,
    },
  });

  const [toolType, setToolType] = useLocalStorage('VDC_OS_TOOL_TYPE', 'Evolve');

  const value = useMemo(
    () => ({
      ...filters,
      toolType,
      timeFrame,
    }),
    [filters, timeFrame, toolType],
  );

  return (
    <ChartFiltersContext value={value}>
      <AnalyticsLandingPageWrapped
        pendingFilters={pendingFilters}
        setToolType={setToolType}
        setFilters={setPendingFilters}
        applyChanges={applyFilters}
        discardChanges={discardPendingFilters}
        hasPendingChanges={hasPendingChanges}
        filtersAreSet={false}
      />
    </ChartFiltersContext>
  );
};
